import React from "react";
import { classNames } from "../../utils/cn";
import { withMarginTw } from "../../styles/withMargin";
import { withPaddingTw } from "../../styles/withPadding";

type TrackingOption =
  | "tighter"
  | "tight"
  | "normal"
  | "wide"
  | "wider"
  | "widest";

const trackingClassMap: Record<TrackingOption, string> = {
  tighter: "tracking-tighter",
  tight: "tracking-tight",
  normal: "tracking-normal",
  wide: "tracking-wide",
  wider: "tracking-wider",
  widest: "tracking-widest",
};

function filterAllowedClassNames(className?: string): string {
  if (!className) return "";
  const tokens = className.split(/\s+/).filter(Boolean);
  const allowed: string[] = [];

  const allowedPrefixes = [
    "whitespace-",
    "tracking-",
    "flex",
    "inline-flex",
    "items-",
    "justify-",
    "gap-",
    "gap-x-",
    "gap-y-",
  ];

  for (const token of tokens) {
    if (allowedPrefixes.some((p) => token.startsWith(p))) {
      allowed.push(token);
    }
  }

  return allowed.join(" ");
}

export interface TableHeaderProps
  extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, "className"> {
  margin?: string;
  padding?: string;
  textAlign?: "left" | "center" | "right";
  nowrap?: boolean;
  tracking?: TrackingOption;
  className?: string; // will be filtered to allowed tokens only
}

export const TableHeader: React.FC<TableHeaderProps> = (props) => {
  const {
    children,
    margin,
    padding,
    textAlign = "left",
    nowrap,
    tracking,
    className,
    ...rest
  } = props;

  const marginClasses = withMarginTw({ margin: margin ?? "" });
  const paddingClasses = withPaddingTw({ padding: padding ?? "" });

  const alignClass =
    textAlign === "left"
      ? "text-left"
      : textAlign === "center"
        ? "text-center"
        : "text-right";

  const trackingClass = tracking ? trackingClassMap[tracking] : "";
  const nowrapClass = nowrap ? "whitespace-nowrap" : "";

  const safeExtraClasses = filterAllowedClassNames(className);

  // Base text styling is locked; caller cannot override these
  const baseTextClasses = "text-sm font-bold text-persian-990";

  return (
    <th
      {...rest}
      className={classNames(
        // caller-provided allowed classes come first
        safeExtraClasses,
        // spacing controls via props
        marginClasses,
        paddingClasses,
        // semantic alignment and allowed text utility controls
        alignClass,
        trackingClass,
        nowrapClass,
        // locked text styling last to prevent overrides
        baseTextClasses,
      )}
    >
      {children}
    </th>
  );
};

export default TableHeader;
